import { memo, useContext, useEffect, useRef, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import throttle from 'lodash/throttle'
import { DocumentTypes } from 'builder/modules/constants'
import { GuestResumeContext } from 'builder/context/GuestResumeContext'
import PdfPreview from 'builder/components/PdfPreview'
import templateHeightFactorSelector from '../Helper/utils/templateHeightFactorSelector'
import { PreviewGuest } from '../Preview/PreviewGuest'
import { Container, PreviewBox, PreviewWrapper } from './styles'
import TopbarLayout from './components/TopbarLayout'
import BottomBarLayout from './components/BottomBarLayout'

type Props = {
  currentPage: number
  totalPagesNumber: number
  isSyncing: boolean
  onPaginationChange: (state: { currentPage: number; totalPagesCount: number }) => void
  handleFullSize: () => void
  handleDownloadButtonMenuOpen?: () => void
}

const PreviewPanelGuest = (props: Props): JSX.Element => {
  const {
    handleFullSize,
    currentPage,
    onPaginationChange,
    totalPagesNumber,
    isSyncing,
    handleDownloadButtonMenuOpen,
  } = props
  const previewRef = useRef<PdfPreview>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)

  const { toggleEmailRequiredError, resume } = useContext(GuestResumeContext)
  const { template } = resume
  const currentDocument = resume

  const handlePreviewResize = throttle(() => {
    if (!containerRef.current) return
    const pdfHeightFactor = templateHeightFactorSelector(currentDocument)
    const previewHeight = containerRef.current.offsetHeight
    const previewWidth = containerRef.current.offsetWidth

    const width = Math.min(previewWidth, previewHeight * pdfHeightFactor)
    setWidth(Math.round(width))
    setHeight(Math.round(width / pdfHeightFactor))

    if (!previewRef.current) return
    previewRef.current.resize()
  }, 50)

  useEffect(() => {
    window.addEventListener('resize', handlePreviewResize)
    handlePreviewResize()
    return () => {
      window.removeEventListener('resize', handlePreviewResize)
    }
  }, [handlePreviewResize])

  const handleActions = () => {
    toggleEmailRequiredError()
  }

  const handleDownloadAction = () => {
    trackInternalEvent('click_download_resume_button', {
      source: 'resume_editor',
      template,
      format: 'pdf',
      score: resume?.resumeScore,
    })

    toggleEmailRequiredError()
  }

  return (
    <Container id="preview-panel-container">
      <PreviewBox ref={containerRef}>
        <PreviewWrapper>
          <TopbarLayout
            {...{
              width,
              type: DocumentTypes.resume,
              documentId: currentDocument.id,
              isDownloading: false,
              handleFullSize,
              customAction: handleActions,
              handleDownloadDocument: handleDownloadAction,
              currentDocument,
              isRbpStepFlowActive: true,
              shouldShowSelectTemplateButton: true,
              handleDownloadButtonMenuOpen,
            }}
          />
          {!!width && (
            <PreviewGuest
              {...{
                height,
                width,
                currentDocument,
                onPaginationChange,
                handleUnlockClick: handleActions,
                analyticsLabel: 'preview_panel',
              }}
            />
          )}
          <BottomBarLayout
            {...{
              type: DocumentTypes.resume,
              currentPage,
              totalPagesNumber,
              documentId: currentDocument.id,
              previewRef,
              customAction: handleActions,
              currentDocument,
              isDownloading: false,
              isOnline: true,
              isPrefilling: false,
              isSyncing,
            }}
          />
        </PreviewWrapper>
      </PreviewBox>
    </Container>
  )
}

export default memo(PreviewPanelGuest)
